import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Link,
    useTheme,
} from '@mui/material';
import {
    Email,
    Groups,
    People,
} from '@mui/icons-material';
import { navItems } from '../data';
import { paths } from '../routes/paths';
import { Link as RouterLink } from 'react-router-dom';
import { logEvent } from '../utils/analytics';

const footerLinkSx = {
    color: 'grey.400',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 'var(--touch-min)',
    py: 0.5,
    '&:hover': { color: 'white' },
};

const footerPolicyLinkSx = {
    ...footerLinkSx,
    fontSize: '0.875rem',
    '&:hover': {
        color: 'white',
        textDecoration: 'underline',
    },
};

const Footer: React.FC = () => {
    const theme = useTheme();

    return (
        <Box component="footer" sx={{ bgcolor: 'grey.900', color: 'white' }}>
            <Container sx={{ py: 6 }}>
                <Grid container spacing={4}>
                    <Grid size={{ xs: 12, md: 3 }} >
                        <Box sx={{ mb: 2 }}>
                            <Link href="#" sx={{ ...footerLinkSx, textDecoration: 'none' }}>
                                <Typography
                                    component="span"
                                    variant="h5"
                                    sx={{
                                        fontWeight: 'bold',
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent'
                                    }}
                                >
                                    AttendIn
                                </Typography>
                            </Link>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{ color: 'grey.400', mb: 3 }}
                        >
                            Transforming networking for any event through personalised engagement and meaningful connections.
                        </Typography>

                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Quick Links</Typography>
                        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                            {navItems.map((item, index) => (
                                <Box component="li" key={index} sx={{ mb: 1 }}>
                                    <Link
                                        href={item.href}
                                        sx={footerLinkSx}
                                        onClick={() => {
                                            logEvent('Footer', 'click', item.label);
                                        }
                                        }
                                    >
                                        {item.label}
                                    </Link>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Discover</Typography>
                        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                            <Box component="li" sx={{ mb: 1 }}>
                                <Link
                                    component={RouterLink}
                                    to={`${paths.public.search}?type=communities`}
                                    sx={{
                                        ...footerLinkSx,
                                        display: 'inline-flex',
                                        gap: 0.75,
                                    }}
                                    onClick={() => logEvent('Footer', 'click', 'Discover Communities')}
                                >
                                    <Groups sx={{ fontSize: 16 }} />
                                    Discover Communities
                                </Link>
                            </Box>
                            <Box component="li" sx={{ mb: 1 }}>
                                <Link
                                    component={RouterLink}
                                    to={`${paths.public.search}?type=people`}
                                    sx={{
                                        ...footerLinkSx,
                                        display: 'inline-flex',
                                        gap: 0.75,
                                    }}
                                    onClick={() => logEvent('Footer', 'click', 'Discover People')}
                                >
                                    <People sx={{ fontSize: 16 }} />
                                    Discover People
                                </Link>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }} >
                        <Typography variant="h6" sx={{ mb: 2 }}>Contact Us</Typography>
                        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, space: 2 }}>
                            <Box component="li" sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Email sx={{ color: 'grey.400', mr: 1.5, mt: 0.5 }} />
                                <Typography sx={{ color: 'grey.400' }}>hello@attendin.io</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        borderTop: 1,
                        borderColor: 'grey.800',
                        mt: 4,
                        pt: 4,
                        mb: 0,
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'grey.500' }}>
                        &copy; {new Date().getFullYear()} AttendIn. All rights reserved.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                        <Link
                            component={RouterLink}
                            to={paths.public.privacyPolicyPage}
                            sx={footerPolicyLinkSx}
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            component={RouterLink}
                            to={paths.public.termsOfServicePage}
                            sx={footerPolicyLinkSx}
                        >
                            Terms of Service
                        </Link>
                        <Link
                            component={RouterLink}
                            to={paths.public.signupTermsPage}
                            sx={footerPolicyLinkSx}
                        >
                            Signup Terms
                        </Link>
                        <Link
                            component={RouterLink}
                            to={paths.public.cookiePolicyPage}
                            sx={footerPolicyLinkSx}
                        >
                            Cookie Policy
                        </Link>
                        <Link
                            component={RouterLink}
                            to={paths.public.accountDeletionPage}
                            sx={footerPolicyLinkSx}
                        >
                            Account Deletion
                        </Link>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;